import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiChevronDown, FiChevronRight } from "react-icons/fi"
import { DateTime } from "luxon"

import "./index.scss"
import useT from "utils/hooks/useT"
import { limsChangeControl, limsWorklistTemplates, limsSstTemplates, limsForskrifter } from "state_management"
import { kindLabelKey, PENDING_STATES } from "state_management/lims/limsChangeControl"
import ApprovalActions from "./ApprovalActions"
import VersionDetail from "./VersionDetail"

// LIMS › Change control (SPEC-058 B, Plannertech-user#4115) — the ONE place master data is
// approved. One queue over every pending version across the four F7 families (specifications,
// worklist templates, SST templates, analysis documents), open to every LIMS user: everyone
// sees what is waiting and where it stands; only the qualified signer's button is active, the
// rest are ghosted with the reason (SPEC-022 doctrine). Until SPEC-058 the specification's approve
// buttons lived only on the planner-only Setup page — a MEMBER-role reviewer could never reach an
// approval the server allowed — and the three registers each had their own copy; those are gone.
//
// Rows expand to show WHAT is being signed (diff / steps / criteria / file) and offer the
// read-only view of the analysis definition for context. `?item=<id>` (from the reviews inbox
// or a register's "awaiting approval" link) highlights and expands that row.
export default function ChangeControl() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const focusId = searchParams.get("item")
  const items = useSelector(limsChangeControl.selectPending)
  const hasFetched = useSelector(limsChangeControl.selectHasFetched)
  const members = useSelector((state) => state.members)
  // The row the inbox / a register pointed at (?item=) starts expanded; a click overrides it.
  const [manualExpandedId, setManualExpandedId] = useState(undefined)
  const expandedId = manualExpandedId === undefined ? focusId : manualExpandedId

  useEffect(() => {
    dispatch(limsChangeControl.fetch())
    // The detail panels read the version CONTENT from each family's slice.
    dispatch(limsWorklistTemplates.fetch())
    dispatch(limsSstTemplates.fetch())
    dispatch(limsForskrifter.fetch())
  }, [dispatch])

  const initials = (item) =>
    item.createdByInitials || (item.createdBy && members[item.createdBy] && members[item.createdBy].initials) || "—"
  const when = (item) => (item.createdAt ? DateTime.fromISO(item.createdAt).toFormat("yyyy-MM-dd HH:mm") : "—")
  const stateLabelKey = (state) =>
    state === PENDING_STATES.REVIEWER_APPROVED ? "limsForskriftStatusReviewerApproved" : "limsForskriftStatusDraft"
  const stateClass = (state) => (state === PENDING_STATES.REVIEWER_APPROVED ? "is-reviewer" : "is-draft")

  return (
    <div className="LimsChangeControl">
      <div className="LimsChangeControl-header">
        <h1 className="LimsChangeControl-title">{t("limsChangeControl")}</h1>
      </div>
      <p className="LimsChangeControl-intro">{t("limsChangeControlIntro")}</p>

      {hasFetched && items.length === 0 ? (
        <p className="LimsChangeControl-empty">{t("limsChangeControlEmpty")}</p>
      ) : (
        <Table hover responsive className="LimsChangeControl-table">
          <thead>
            <tr>
              <th></th>
              <th>{t("limsChangeControlKind")}</th>
              <th>{t("name")}</th>
              <th>{t("limsForskriftVersion")}</th>
              <th>{t("limsVersionBuiltOnHeader")}</th>
              <th>{t("limsForskriftStatus")}</th>
              <th>{t("limsChangeControlNextStep")}</th>
              <th>{t("limsChangeControlCreatedBy")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const expanded = expandedId === item.id
              const focused = focusId === item.id
              return (
                <ChangeControlRow
                  key={item.id}
                  item={item}
                  expanded={expanded}
                  focused={focused}
                  onToggle={() => setManualExpandedId(expanded ? null : item.id)}
                  initials={initials(item)}
                  when={when(item)}
                  stateLabel={t(stateLabelKey(item.state))}
                  stateClass={stateClass(item.state)}
                  kindLabel={t(kindLabelKey(item.kind))}
                  nextStep={t(item.requiredCapability === "supervisor" ? "limsCapSupervisor" : "limsCapReviewer")}
                  t={t}
                />
              )
            })}
          </tbody>
        </Table>
      )}
    </div>
  )
}

function ChangeControlRow({ item, expanded, focused, onToggle, initials, when, stateLabel, stateClass, kindLabel, nextStep, t }) {
  return (
    <>
      <tr className={"LimsChangeControl-row" + (focused ? " is-focused" : "")} onClick={onToggle}>
        <td className="LimsChangeControl-chevron">{expanded ? <FiChevronDown /> : <FiChevronRight />}</td>
        <td>
          <span className="LimsChangeControl-kind">{kindLabel}</span>
        </td>
        <td className="LimsChangeControl-name">{item.name}</td>
        <td>v{item.versionNumber}</td>
        <td>{item.basedOnVersionNumber ? `v${item.basedOnVersionNumber}` : "—"}</td>
        <td>
          <span className={"LimsForskrifter-status " + stateClass}>{stateLabel}</span>
        </td>
        <td>{nextStep}</td>
        <td>
          {initials}
          <span className="LimsChangeControl-when"> · {when}</span>
        </td>
        <td className="LimsChangeControl-actions" onClick={(e) => e.stopPropagation()}>
          <Button variant="link" size="sm" onClick={onToggle}>
            {expanded ? t("limsChangeControlDetailsHide") : t("limsChangeControlDetails")}
          </Button>
          <ApprovalActions item={item} />
        </td>
      </tr>
      {expanded && (
        <tr className="LimsChangeControl-detail-row">
          <td colSpan={9}>
            <VersionDetail item={item} />
          </td>
        </tr>
      )}
    </>
  )
}
